import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Form from '@rjsf/mui';
import type { RJSFSchema, StrictRJSFSchema, TemplatesType, UiSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import isEqual from 'lodash/isEqual';
import { useSnackbar } from 'notistack';
import type { Location } from 'react-router-dom';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import GridObjectFieldTemplate from '~/components/Common/Forms/GridObjectFieldTemplate';
import SimpleArrayFieldItemTemplate from '~/components/Common/Forms/SimpleArrayFieldItemTemplate';
import SimpleArrayFieldTemplate from '~/components/Common/Forms/SimpleArrayFieldTemplate';
import { useUnsavedChangesGuard } from '~/components/Flights/Charter/hooks/useUnsavedChangesGuard';

import { useUpdateAccount } from '~/hooks/apis/users/useUpdateAccount';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { AccountType } from '~/services/UsersService';

import {
  type DisallowedRole,
  type RolesAndVendors,
  getRolesAndVendorsPayload,
  hasRolesAndVendorsErrors,
  normalizeRolesAndVendors,
  requiresVendors,
  validateRolesAndVendors,
} from '~/utils/accountRoles';
import { canChangeAccountRoles } from '~/utils/adminPermission';

import { SECURITY_PANEL_HEADER_SX, getErrorMessage } from './utils';

const FORM_TEMPLATES: Partial<TemplatesType> = {
  ObjectFieldTemplate: GridObjectFieldTemplate,
  ArrayFieldTemplate: SimpleArrayFieldTemplate,
  ArrayFieldItemTemplate: SimpleArrayFieldItemTemplate,
};

const VENDORS_SCHEMA: StrictRJSFSchema = {
  type: 'array',
  title: 'Vendor IDs',
  items: { type: 'string', title: 'Vendor' },
};

type RoleItemsSchema = StrictRJSFSchema & { enumNames?: Array<string> };

/**
 * Builds the role dropdown options from the user schema. Disallowed roles are hidden unless the
 * account already has them, so an existing assignment is still displayed (and flagged on save).
 */
function getRoleOptions(
  rolesSchema: StrictRJSFSchema | undefined,
  selectedRoles: Array<string>,
  disallowed: Array<DisallowedRole>,
) {
  const itemsSchema = (rolesSchema?.items ?? { type: 'string' }) as RoleItemsSchema;
  const hiddenRoles = disallowed.map(({ role }) => role).filter((role) => !selectedRoles.includes(role));
  const roleEnum = Array.isArray(itemsSchema.enum) ? itemsSchema.enum : [];
  const visibleIndexes = roleEnum
    .map((role, index) => ({ role, index }))
    .filter(({ role }) => !(typeof role === 'string' && hiddenRoles.includes(role)))
    .map(({ index }) => index);
  const { enumNames, ...items } = itemsSchema;

  return {
    items: {
      ...items,
      title: 'Role',
      ...(Array.isArray(itemsSchema.enum) && { enum: visibleIndexes.map((index) => roleEnum[index]) }),
    },
    enumNames: Array.isArray(enumNames) ? visibleIndexes.map((index) => enumNames[index]) : undefined,
  };
}

type Props = {
  accountId: string;
  accountType?: AccountType;
  accountEnabled: boolean;
  roles: Array<string>;
  vendors?: Array<string>;
  rolesSchema?: StrictRJSFSchema;
  disallowedRoles?: Array<DisallowedRole>;
  onRolesAndVendorsUpdated: (rolesAndVendors: RolesAndVendors) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
};

export default function RolesAndVendorsField({
  accountId,
  accountType,
  accountEnabled,
  roles,
  vendors,
  rolesSchema,
  disallowedRoles = [],
  onRolesAndVendorsUpdated,
  onUnsavedChangesChange,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentUser } = useCurrentUser();
  const updateAccountMutation = useUpdateAccount(accountId);

  const savedRolesAndVendors = useMemo<RolesAndVendors>(
    () => ({ roles: roles ?? [], vendors: vendors ?? [] }),
    [roles, vendors],
  );
  const [draft, setDraft] = useState<RolesAndVendors>(savedRolesAndVendors);
  const [showErrors, setShowErrors] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<Location | null>(null);

  // The saved values change after a successful save or when another account is opened.
  useEffect(() => {
    setDraft(savedRolesAndVendors);
    setShowErrors(false);
  }, [savedRolesAndVendors]);

  const hasPermission = canChangeAccountRoles(currentUser);
  const isDisabled = !hasPermission || updateAccountMutation.isPending;
  // Compared after normalising, so an added but unselected row is not treated as a change.
  const hasUnsavedChanges = !isEqual(normalizeRolesAndVendors(draft), normalizeRolesAndVendors(savedRolesAndVendors));
  const errors = validateRolesAndVendors(draft, {
    accountType,
    accountEnabled,
    disallowedRoles,
    savedRoles: savedRolesAndVendors.roles,
  });
  const errorMessages = [...(errors.roles ?? []), ...(errors.vendors ?? [])];

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  // The panel unmounts when switching sections, which discards the draft.
  useEffect(() => () => onUnsavedChangesChange?.(false), [onUnsavedChangesChange]);

  const handleNavigationAttempt = useCallback((location: Location) => setPendingNavigation(location), []);
  const navigationGuard = useUnsavedChangesGuard({
    hasUnsavedChanges,
    onNavigationAttempt: handleNavigationAttempt,
  });

  const { schema, uiSchema } = useMemo(() => {
    const roleOptions = getRoleOptions(rolesSchema, draft.roles, disallowedRoles);
    const formSchema: RJSFSchema = {
      type: 'object',
      properties: {
        roles: { type: 'array', title: 'Roles', items: roleOptions.items },
        ...(requiresVendors(draft.roles) && { vendors: VENDORS_SCHEMA }),
      },
    };
    const formUiSchema: UiSchema = {
      // Saving is handled by the panel's own button.
      'ui:submitButtonOptions': { norender: true },
      roles: {
        'ui:options': { roleLayout: true, compact: true },
        items: { 'ui:enumNames': roleOptions.enumNames },
      },
      vendors: { 'ui:options': { compact: true, orderable: false } },
    };

    return { schema: formSchema, uiSchema: formUiSchema };
  }, [rolesSchema, draft.roles, disallowedRoles]);

  const handleDiscard = () => {
    setDraft(savedRolesAndVendors);
    setShowErrors(false);
  };

  const handleSave = async () => {
    if (hasRolesAndVendorsErrors(errors)) {
      setShowErrors(true);
      return;
    }

    const payload = getRolesAndVendorsPayload(draft);

    try {
      const updatedAccount = await updateAccountMutation.mutateAsync(payload);
      onRolesAndVendorsUpdated({
        roles: updatedAccount?.roles ?? payload.roles,
        vendors: updatedAccount?.vendors ?? payload.vendors ?? [],
      });
      enqueueSnackbar('Roles and vendors updated', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Unable to update roles and vendors: ${getErrorMessage(error)}`, { variant: 'error' });
    }
  };

  return (
    <Paper component="section" variant="outlined" sx={{ p: 3 }}>
      <PageSubheader title="Roles & Vendors" showDivider={false} sx={SECURITY_PANEL_HEADER_SX} />

      {!hasPermission && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Changing roles and vendors requires the admin-user or employee role.
        </Alert>
      )}

      <Form
        formData={draft}
        schema={schema}
        uiSchema={uiSchema}
        templates={FORM_TEMPLATES}
        validator={validator}
        disabled={isDisabled}
        onChange={({ formData }) => setDraft(formData)}
        showErrorList={false}
        noHtml5Validate
      />

      {showErrors && errorMessages.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessages.map((message) => (
            <Typography key={message} variant="body2">
              {message}
            </Typography>
          ))}
        </Alert>
      )}

      {hasPermission && (
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasUnsavedChanges || updateAccountMutation.isPending}
          >
            {updateAccountMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="text"
            onClick={handleDiscard}
            disabled={!hasUnsavedChanges || updateAccountMutation.isPending}
          >
            Discard changes
          </Button>
          {hasUnsavedChanges && (
            <Typography variant="body2" color="text.secondary">
              You have unsaved changes.
            </Typography>
          )}
        </Stack>
      )}

      <Dialog open={pendingNavigation !== null} onClose={() => setPendingNavigation(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Your changes to roles and vendors have not been saved. Leaving this page will discard them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingNavigation(null)}>Keep editing</Button>
          <Button
            variant="contained"
            onClick={() => {
              const target = pendingNavigation;
              setPendingNavigation(null);
              if (target) {
                navigationGuard.releaseAndNavigate(target);
              }
            }}
          >
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
